import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  InputAdornment,
} from '@mui/material';
import XmlResponseViewer from './XmlResponseViewer';
import { AccountType } from '../../types/auth';
import { Company } from '../../types/company';
import { companyApi } from '../../services/api/companyApi';
import { useAuthStore } from '../../store/authStore';
import {
  ExpandMore as ExpandMoreIcon,
  Send as SendIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import LicenseeFilter from '../common/LicenseeFilter';
import { CarbonCareDatePicker } from '../shared/CarbonCareDatePicker';

const XML_API_URL = process.env.REACT_APP_XML_API_URL || 'http://localhost:8080/xml/calc';

export type FuelType =
  | 'Diesel'
  | 'B100'
  | 'Electric'
  | 'Hydrogen'
  | 'HVO'
  | 'HVO32'
  | 'LNG'
  | '';

interface LegData {
  id: string;
  type: string;
  fromAddressType: string;
  fromPostalCode: string;
  fromTwoLetterCountry: string;
  fromCountry: string;
  fromCity: string;
  fromStreet: string;
  fromAirPortCode: string;
  fromSeaPortCode: string;
  fromLatitude: string;
  fromLongitude: string;
  toAddressType: string;
  toPostalCode: string;
  toTwoLetterCountry: string;
  toCountry: string;
  toCity: string;
  toStreet: string;
  toAirPortCode: string;
  toSeaPortCode: string;
  toLatitude: string;
  toLongitude: string;
  distance?: number;
  fuelType: FuelType;
  isFreighter: boolean;
  acType: string;
  ferryType: string;
  iwwType: string;
  calculatePreAndPostTransport: string;
  truckType: string;
  railType: string;
  loadFactor: string;
}

interface ShipmentData {
  apiVersion: string;
  apiKey: string;
  shipmentId: string;
  branch: string;
  subCompany: string;
  customerReference: string;
  reference: string;
  dateOfShipment: string;
  commodity: string;
  weight: string;
  weightUnit: string;
  volume: string;
  volumeUnit: string;
  cooling: boolean;
  legs: LegData[];
}

const getCurrentDateTimeLocal = (): string => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  const local = new Date(now.getTime() - offset);
  return local.toISOString().slice(0, 16);
};

const makeDefaultLeg = (): LegData => ({
  id: Date.now().toString(),
  type: 'ROAD',
  fromAddressType: 'Address',
  fromPostalCode: '',
  fromTwoLetterCountry: '',
  fromCountry: '',
  fromCity: '',
  fromStreet: '',
  fromAirPortCode: '',
  fromSeaPortCode: '',
  fromLatitude: '',
  fromLongitude: '',
  toAddressType: 'Address',
  toPostalCode: '',
  toTwoLetterCountry: '',
  toCountry: '',
  toCity: '',
  toStreet: '',
  toAirPortCode: '',
  toSeaPortCode: '',
  toLatitude: '',
  toLongitude: '',
  isFreighter: false,
  acType: '',
  ferryType: '',
  iwwType: '',
  fuelType: '',
  calculatePreAndPostTransport: '',
  truckType: '',
  railType: '',
  loadFactor: '',
});

// Per-prefix address values, resolved once before validation.
interface AddressVals {
  postalCode: string; twoLetterCountry: string; country: string; city: string; street: string;
  airPortCode: string; seaPortCode: string; latitude: string; longitude: string;
}

const resolveAddressVals = (leg: LegData, prefix: 'from' | 'to'): AddressVals =>
  prefix === 'from'
    ? { postalCode: leg.fromPostalCode, twoLetterCountry: leg.fromTwoLetterCountry, country: leg.fromCountry, city: leg.fromCity, street: leg.fromStreet, airPortCode: leg.fromAirPortCode, seaPortCode: leg.fromSeaPortCode, latitude: leg.fromLatitude, longitude: leg.fromLongitude }
    : { postalCode: leg.toPostalCode, twoLetterCountry: leg.toTwoLetterCountry, country: leg.toCountry, city: leg.toCity, street: leg.toStreet, airPortCode: leg.toAirPortCode, seaPortCode: leg.toSeaPortCode, latitude: leg.toLatitude, longitude: leg.toLongitude };

// Pure: is the chosen address type fully specified for the given values?
const isAddressTypeValid = (addrType: string, v: AddressVals): boolean => {
  if (addrType === 'Address') {
    return [v.postalCode, v.twoLetterCountry, v.country, v.city, v.street].some(f => f && f.trim() !== '');
  }
  if (addrType === 'AirPortCode') return !!v.airPortCode;
  if (addrType === 'SeaPortCode') return !!v.seaPortCode;
  if (addrType === 'Coordinates') return !!(v.latitude && v.longitude);
  return false;
};

// First leg address that isn't fully specified, described for the error alert.
const firstInvalidAddressMessage = (legs: LegData[]): string => {
  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i];
    for (const prefix of ['from', 'to'] as const) {
      const addrType = prefix === 'from' ? leg.fromAddressType : leg.toAddressType;
      if (addrType && isAddressTypeValid(addrType, resolveAddressVals(leg, prefix))) continue;
      return `Leg ${i + 1}: please complete the ${prefix === 'from' ? 'From' : 'To'} address.`;
    }
  }
  return '';
};

const controlLabel = (el: Element): string =>
  (el.closest('.MuiFormControl-root')?.querySelector('label')?.textContent ?? '')
    .replace(/\s*\*$/, '')
    .trim();

// The form is noValidate, so the browser never points at the offending control.
// Resolve the first one that fails constraint validation and name it instead.
const firstInvalidFieldMessage = (form: HTMLFormElement): string => {
  for (const el of Array.from(form.elements)) {
    const control = el as HTMLInputElement;
    if (typeof control.checkValidity !== 'function' || control.checkValidity()) continue;
    const name = controlLabel(control) || 'a mandatory field';
    return control.validity.valueMissing
      ? `Please fill in "${name}".`
      : `"${name}" has an invalid value.`;
  }
  return 'Please fill in all mandatory fields.';
};

// ── Shared accordion style: flat border, no shadow, no double divider ──────
const sectionAccordionSx = {
  elevation: 0,
  sx: {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px !important',
    boxShadow: 'none',
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: 0 },
  },
};

const legAccordionSx = {
  elevation: 0,
  sx: {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px !important',
    boxShadow: 'none',
    mb: 1.5,
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: '0 0 12px 0' },
  },
};

const MIN_SHIPMENT_DATE = new Date(2000, 0, 1);

const getMaxDateBoundary = (): Date => {
  const nextYear = new Date().getFullYear() + 1;
  return new Date(nextYear, 11, 31);
};

const fuelLetterMap: Record<string, string> = {
  Diesel: 'D', B100: 'B', Electric: 'E', Hydrogen: 'H', HVO: 'V', HVO32: 'O', LNG: 'L',
};

const buildFcpTruckCode = (base: string, fuel: FuelType): string =>
  base && fuel ? base + (fuelLetterMap[fuel] || '') : base || '';

// Per-leg context shared by all transport-mode XML builders.
interface LegXmlCtx {
  from: string;
  to: string;
  dist: string;
  isV32: boolean;
}

const buildAirLegXml = (leg: LegData, c: LegXmlCtx): string => `<Air>
              ${c.dist}
              <From>${c.from}</From>
              <To>${c.to}</To>
              ${!c.isV32 && leg.isFreighter ? '<IsFreighter>true</IsFreighter>' : ''}
              ${leg.acType ? `<AirType>${leg.acType}</AirType>` : ''}
              ${leg.calculatePreAndPostTransport ? `<CalculatePreAndPostTransport>${leg.calculatePreAndPostTransport}</CalculatePreAndPostTransport>` : ''}
            </Air>`;

const buildRoadLegXml = (leg: LegData, c: LegXmlCtx): string => {
  const code = c.isV32 ? buildFcpTruckCode(leg.truckType, leg.fuelType) : leg.truckType;
  return `<Truck>
              ${c.dist}
              <From>${c.from}</From>
              <To>${c.to}</To>
              ${code ? `<Type>${code}</Type>` : ''}
              ${leg.fuelType ? `<FuelType>${leg.fuelType}</FuelType>` : ''}
              ${leg.loadFactor ? `<LoadFactor>${leg.loadFactor}</LoadFactor>` : ''}
            </Truck>`;
};

const buildRailLegXml = (leg: LegData, c: LegXmlCtx): string => {
  const rt = c.isV32 ? leg.railType : '';
  const tc = rt === 'RAILRH' && leg.truckType ? leg.truckType + 'D' : '';
  return `<Rail>
              ${c.dist}
              <From>${c.from}</From>
              <To>${c.to}</To>
              ${rt ? `<Type>${rt}</Type>` : ''}
              ${tc ? `<TruckType>${tc}</TruckType>` : ''}
            </Rail>`;
};

const buildSeaLegXml = (_leg: LegData, c: LegXmlCtx): string =>
  `<Sea>${c.dist}<From>${c.from}</From><To>${c.to}</To></Sea>`;

const buildIwwLegXml = (leg: LegData, c: LegXmlCtx): string => `<Iww>
              ${c.dist}
              <From>${c.from}</From>
              <To>${c.to}</To>
              ${leg.iwwType ? `<Type>${leg.iwwType}</Type>` : ''}
            </Iww>`;

const buildFerryLegXml = (leg: LegData, c: LegXmlCtx): string => `<Ferry>
              ${c.dist}
              <From>${c.from}</From>
              <To>${c.to}</To>
              ${leg.truckType ? `<TruckType>${leg.truckType}</TruckType>` : ''}
              ${leg.ferryType ? `<Type>${leg.ferryType}</Type>` : ''}
            </Ferry>`;

const buildDefaultLegXml = (_leg: LegData, c: LegXmlCtx): string =>
  `<Truck><From>${c.from}</From><To>${c.to}</To></Truck>`;

type LegXmlBuilder = (leg: LegData, c: LegXmlCtx) => string;

const legXmlBuilders: Record<string, LegXmlBuilder> = {
  AIR: buildAirLegXml,
  ROAD: buildRoadLegXml,
  RAIL: buildRailLegXml,
  SEA: buildSeaLegXml,
  IWW: buildIwwLegXml,
  FERRY: buildFerryLegXml,
};

const XmlApiForm: React.FC = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [xmlResponse, setXmlResponse] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [versionMenuAnchor, setVersionMenuAnchor] = useState<null | HTMLElement>(null);
  const [errorCodesOpen, setErrorCodesOpen] = useState(false);
  const [errorCodes, setErrorCodes] = useState<Record<string, any> | null>(null);
  const [errorCodesLoading, setErrorCodesLoading] = useState(false);

  const [shipmentData, setShipmentData] = useState<ShipmentData>(() => ({
    apiVersion: '3.2',
    apiKey: '',
    shipmentId: '',
    branch: '',
    subCompany: '',
    customerReference: '',
    reference: '',
    dateOfShipment: new Date().toISOString().slice(0, 10) + 'T00:00:00',
    commodity: '',
    weight: '1000',
    weightUnit: 'kg',
    volume: '',
    volumeUnit: '',
    cooling: false,
    legs: [{ ...makeDefaultLeg(), id: '1' }],
  }));

  // ── Constants ─────────────────────────────────────────────────────────────
  const fetchErrorCodes = async () => {
    if (errorCodes) {
      setErrorCodesOpen(true);
      return;
    }
    setErrorCodesLoading(true);
    setErrorCodesOpen(true);
    try {
      const baseUrl = (process.env.REACT_APP_XML_API_URL || 'http://localhost:8080/xml/calc').replace('/xml/calc', '');
      const res = await fetch(`${baseUrl}/api/error-codes`);
      if (res.ok) {
        setErrorCodes(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch error codes', err);
    } finally {
      setErrorCodesLoading(false);
    }
  };

  const legTypes = ['AIR', 'ROAD', 'RAIL', 'SEA', 'IWW', 'FERRY'];
  const addressTypes = ['Address', 'AirPortCode', 'SeaPortCode', 'Coordinates'];
  const weightUnits = ['kg', 'lb'];
  const ferryTypeFCP = ['FROSD', 'FROLD', 'FRXSD', 'FRXLD'];
  const ferryTypeISO = ['roro_large', 'roro_small', 'ropax_large', 'ropax_small'];
  const ferryTypeEN = ['large', 'small'];
  const iwwTypeFCP = ['IM40D', 'IM55D', 'IM80D', 'IM110D', 'IM135D', 'IC185D', 'IP2D', 'IP5D', 'IP6D', 'ITVD', 'ICVD', 'ICVCD'];
  const iwwTypeISO = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];
  const iwwTypeEN = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13'];
  const truckTypes = ['automatic', '3.5T', '7T', '13-18T', '22-36T', '40T'];
  const apiVersions = ['3.2', '3.0'];
  const truckBaseCodes = ['R03', 'R07', 'R18', 'R26', 'R36', 'R44'];

  const truckFuelsByBase: Record<string, FuelType[]> = {
    R03: ['Diesel', 'B100', 'Electric', 'HVO', 'HVO32'],
    R07: ['Diesel', 'B100', 'Electric', 'HVO', 'HVO32'],
    R18: ['Diesel', 'B100', 'Electric', 'HVO', 'HVO32'],
    R26: ['Diesel', 'B100', 'Electric', 'Hydrogen', 'HVO', 'HVO32'],
    R36: ['Diesel', 'B100', 'Electric', 'Hydrogen', 'HVO', 'HVO32', 'LNG'],
    R44: ['Diesel', 'B100', 'Electric', 'Hydrogen', 'HVO', 'HVO32', 'LNG'],
  };

  const airCraftOptions = [
    { value: 'ABA', label: 'BELLY AIRCRAFT' },
    { value: 'AFA', label: 'FREIGHTER' },
  ];

  // ── Permissions ───────────────────────────────────────────────────────────
  const isMasterAdmin = user?.accountType === AccountType.MasterAdmin || user?.accountType === AccountType.CarbonCareAdmin;
  const shouldShowCompanySelector = isMasterAdmin;

  useEffect(() => {
    if (user?.companyId) {
      loadUserCompany(user.companyId);
    }
  }, [user]);

  useEffect(() => {
    if (selectedCompany) {
      updateShipment('apiKey', selectedCompany.apiKey || selectedCompany.api_key || '');
    }
  }, [selectedCompany]);

  const loadUserCompany = async (companyId: number) => {
    try {
      setCompaniesLoading(true);
      setError('');
      const company = await companyApi.getById(companyId);
      setSelectedCompany(company);
    } catch {
      setError('Failed to load your company information. Please try again.');
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleCompanySelect = async (companyId: number) => {
    try {
      setCompaniesLoading(true);
      const company = await companyApi.getById(companyId);
      setSelectedCompany(company);
    } catch {
      setError('Failed to load company details.');
    } finally {
      setCompaniesLoading(false);
    }
  };

  // ── Leg management ────────────────────────────────────────────────────────
  const addLeg = () =>
    setShipmentData(prev => ({ ...prev, legs: [...prev.legs, makeDefaultLeg()] }));

  const removeLeg = (legId: string) =>
    setShipmentData(prev => ({ ...prev, legs: prev.legs.filter(l => l.id !== legId) }));

  const updateLeg = (legId: string, field: keyof LegData, value: any) =>
    setShipmentData(prev => ({
      ...prev,
      legs: prev.legs.map(l => (l.id === legId ? { ...l, [field]: value } : l)),
    }));

  const handleLegTypeChange = (legId: string, newType: string) =>
    setShipmentData(prev => ({
      ...prev,
      legs: prev.legs.map(l =>
        l.id === legId
          ? { ...l, type: newType, truckType: '', fuelType: '', railType: newType === 'RAIL' ? 'RAIL' : '', ferryType: '', acType: '', loadFactor: '' }
          : l
      ),
    }));

  const handleTruckBaseChange = (legId: string, base: string) =>
    setShipmentData(prev => ({
      ...prev,
      legs: prev.legs.map(l => (l.id === legId ? { ...l, truckType: base, fuelType: '' } : l)),
    }));

  const updateShipment = (field: keyof ShipmentData, value: any) => {
    if (field === 'legs') return;
    setShipmentData(prev => ({ ...prev, [field]: value }));
  };

  // ── XML builders ──────────────────────────────────────────────────────────
  const buildAddressXml = (addressType: string, prefix: 'from' | 'to', leg: LegData): string => {
    const v = resolveAddressVals(leg, prefix);
    // port codes and coordinates sit directly under From/To, not inside <Address>
    if (addressType === 'AirPortCode') return `<AirPortCode>${v.airPortCode}</AirPortCode>`;
    if (addressType === 'SeaPortCode') return `<SeaPortCode>${v.seaPortCode}</SeaPortCode>`;
    if (addressType === 'Coordinates') return `<Coordinates><Lat>${v.latitude}</Lat><Long>${v.longitude}</Long></Coordinates>`;
    if (addressType !== 'Address') return '<Address></Address>';
    // omit empty optional tags — the XSD rejects empty typed values
    return `<Address>
                  ${v.postalCode ? `<PostalCode>${v.postalCode}</PostalCode>` : ''}
                  <TwoLetterCountry>${v.twoLetterCountry}</TwoLetterCountry>
                  ${v.country ? `<Country>${v.country}</Country>` : ''}
                  ${v.city ? `<City>${v.city}</City>` : ''}
                  ${v.street ? `<Street>${v.street}</Street>` : ''}
                </Address>`;
  };

  const buildLegXml = (leg: LegData): string => {
    const ctx: LegXmlCtx = {
      from: buildAddressXml(leg.fromAddressType, 'from', leg),
      to: buildAddressXml(leg.toAddressType, 'to', leg),
      dist: leg.distance ? `<Distance><Value>${leg.distance}</Value><Unit>km</Unit></Distance>` : '',
      isV32: shipmentData.apiVersion === '3.2',
    };
    const builder = legXmlBuilders[leg.type] ?? buildDefaultLegXml;
    return builder(leg, ctx);
  };

  const buildXmlRequest = (): string => {
    const legsXml = shipmentData.legs.map(l => `<Leg>${buildLegXml(l)}</Leg>`).join('\n          ');
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<CarbonCareApi Version="${shipmentData.apiVersion}">
  <Request>
    <ApiKey>${shipmentData.apiKey}</ApiKey>
    <Shipments>
      <Shipment Id="${shipmentData.shipmentId || `SHIP-${Date.now()}`}">
        ${shipmentData.branch ? `<Branch>${shipmentData.branch}</Branch>` : ''}
        ${shipmentData.subCompany ? `<SubCompany>${shipmentData.subCompany}</SubCompany>` : ''}
        ${shipmentData.reference ? `<Reference>${shipmentData.reference}</Reference>` : ''}
        ${shipmentData.customerReference ? `<CustomerReference>${shipmentData.customerReference}</CustomerReference>` : ''}
        ${shipmentData.dateOfShipment ? `<DateOfShipment>${shipmentData.dateOfShipment}</DateOfShipment>` : ''}
        <Weight>${shipmentData.weight}</Weight>
        ${shipmentData.weightUnit ? `<WeightUnit>${shipmentData.weightUnit}</WeightUnit>` : ''}
        ${shipmentData.volume ? `<Volume>${shipmentData.volume}</Volume>` : ''}
        ${shipmentData.volume && shipmentData.volumeUnit ? `<VolumeUnit>${shipmentData.volumeUnit}</VolumeUnit>` : ''}
        ${shipmentData.commodity ? `<Commodity>${shipmentData.commodity}</Commodity>` : ''}
        <Cooling>${shipmentData.cooling}</Cooling>
        <Legs>
          ${legsXml}
        </Legs>
      </Shipment>
    </Shipments>
  </Request>
</CarbonCareApi>`;
    // Drop whitespace-only lines left behind by unfilled optional fields
    return xml.split('\n').filter(line => line.trim() !== '').join('\n');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);

    const addressError = firstInvalidAddressMessage(shipmentData.legs);
    if (addressError) {
      setError(addressError);
      return;
    }
    if (!e.currentTarget.checkValidity()) {
      setError(firstInvalidFieldMessage(e.currentTarget));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setXmlResponse('');
    try {
      const response = await fetch(XML_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/xml' },
        body: buildXmlRequest(),
      });
      if (response.ok) {
        const text = await response.text();
        if (text.includes('<IsError>true</IsError>')) {
          setError('XML API failed');
        } else {
          setSuccess('XML API call completed successfully!');
        }
        setXmlResponse(text);
      } else {
        setError(`API call failed (${response.status}): ${await response.text()}`);
      }
    } catch (err: any) {
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Address fields ────────────────────────────────────────────────────────
  const addressTypeLabel = (t: string) => {
    if (t === 'AirPortCode') return 'IATA CODE';
    if (t === 'SeaPortCode') return 'UNLOCODE';
    return t;
  };

  const isAddressValid = (leg: LegData, prefix: 'from' | 'to'): boolean => {
    const addrType = prefix === 'from' ? leg.fromAddressType : leg.toAddressType;
    if (!addrType) return false;
    return isAddressTypeValid(addrType, resolveAddressVals(leg, prefix));
  };

  const renderAddressFields = (leg: LegData, prefix: 'from' | 'to') => {
    const addrType = prefix === 'from' ? leg.fromAddressType : leg.toAddressType;
    const dirLabel = prefix === 'from' ? 'From' : 'To';

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth error={submitted && !addrType}>
            <InputLabel>{dirLabel} Address Type</InputLabel>
            <Select
              value={addrType}
              onChange={(e) => updateLeg(leg.id, `${prefix}AddressType`, e.target.value)}
              label={`${dirLabel} Address Type`}
              required
            >
              {addressTypes.map(t => (
                <MenuItem key={t} value={t}>{addressTypeLabel(t)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {addrType === 'Address' && (
          <>
            <Grid item xs={6}>
              <TextField fullWidth label="Postal Code"
                value={prefix === 'from' ? leg.fromPostalCode : leg.toPostalCode}
                onChange={(e) => updateLeg(leg.id, `${prefix}PostalCode`, e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Country Code"
                inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                value={prefix === 'from' ? leg.fromTwoLetterCountry : leg.toTwoLetterCountry}
                onChange={(e) => {
                  const val = e.target.value.replaceAll(/[^a-zA-Z]/g, '').toUpperCase();
                  updateLeg(leg.id, `${prefix}TwoLetterCountry`, val);
                }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Country"
                value={prefix === 'from' ? leg.fromCountry : leg.toCountry}
                onChange={(e) => {
                  if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                    updateLeg(leg.id, `${prefix}Country`, e.target.value);
                  }
                }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="City"
                value={prefix === 'from' ? leg.fromCity : leg.toCity}
                onChange={(e) => {
                  if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                    updateLeg(leg.id, `${prefix}City`, e.target.value);
                  }
                }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Street"
                value={prefix === 'from' ? leg.fromStreet : leg.toStreet}
                onChange={(e) => updateLeg(leg.id, `${prefix}Street`, e.target.value)} />
            </Grid>
          </>
        )}

        {addrType === 'AirPortCode' && (
          <Grid item xs={12}>
            <TextField fullWidth label="IATA Code" placeholder="e.g., JFK, FRA"
              inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
              value={prefix === 'from' ? leg.fromAirPortCode : leg.toAirPortCode}
              onChange={(e) => updateLeg(leg.id, `${prefix}AirPortCode`, e.target.value.toUpperCase())} />
          </Grid>
        )}

        {addrType === 'SeaPortCode' && (
          <Grid item xs={12}>
            <TextField fullWidth label="UNLOCODE" placeholder="e.g., DEHAM, NLRTM"
              inputProps={{ maxLength: 5, style: { textTransform: 'uppercase' } }}
              value={prefix === 'from' ? leg.fromSeaPortCode : leg.toSeaPortCode}
              onChange={(e) => updateLeg(leg.id, `${prefix}SeaPortCode`, e.target.value.toUpperCase())} />
          </Grid>
        )}

        {addrType === 'Coordinates' && (
          <>
            <Grid item xs={6}>
              <TextField fullWidth label="Latitude *" type="number"
                value={prefix === 'from' ? leg.fromLatitude : leg.toLatitude}
                onChange={(e) => updateLeg(leg.id, `${prefix}Latitude`, e.target.value)}
                sx={{ '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' }, '& input[type=number]': { MozAppearance: 'textfield' } }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Longitude *" type="number"
                value={prefix === 'from' ? leg.fromLongitude : leg.toLongitude}
                onChange={(e) => updateLeg(leg.id, `${prefix}Longitude`, e.target.value)}
                sx={{ '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' }, '& input[type=number]': { MozAppearance: 'textfield' } }} />
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  // ── Transport-specific fields ─────────────────────────────────────────────
  const renderTransportFields = (leg: LegData) => {
    const isV32 = shipmentData.apiVersion === '3.2';

    if (leg.type === 'AIR') {
      return isV32 ? (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={submitted && !leg.acType}>
            <InputLabel>Aircraft Type</InputLabel>
            <Select value={leg.acType} onChange={(e) => updateLeg(leg.id, 'acType', e.target.value)} label="Aircraft Type">
              {airCraftOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      ) : (
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={<Checkbox checked={leg.isFreighter} onChange={(e) => updateLeg(leg.id, 'isFreighter', e.target.checked)} />}
            label="Is Freighter"
          />
        </Grid>
      );
    }

    if (leg.type === 'ROAD') {
      if (isV32) {
        const availableFuels = leg.truckType ? (truckFuelsByBase[leg.truckType] || []) : [];
        return (
          <>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Truck Type</InputLabel>
                <Select value={leg.truckType} onChange={(e) => handleTruckBaseChange(leg.id, e.target.value)} label="Truck Type">
                  {truckBaseCodes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled={!leg.truckType}>
                <InputLabel>Fuel Type</InputLabel>
                <Select value={leg.fuelType} onChange={(e) => updateLeg(leg.id, 'fuelType', e.target.value)} label="Fuel Type">
                  {availableFuels.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Load Factor (%)" type="number" value={leg.loadFactor}
                onChange={(e) => updateLeg(leg.id, 'loadFactor', e.target.value)}
                inputProps={{ min: 0, max: 100, step: 'any' }} />
            </Grid>
          </>
        );
      }
      return (
        <>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Truck Type</InputLabel>
              <Select value={leg.truckType} onChange={(e) => updateLeg(leg.id, 'truckType', e.target.value)} label="Truck Type">
                {truckTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Load Factor (%)" type="number" value={leg.loadFactor}
              onChange={(e) => updateLeg(leg.id, 'loadFactor', e.target.value)}
              inputProps={{ min: 0, max: 100, step: 'any' }} />
          </Grid>
        </>
      );
    }

    if (leg.type === 'RAIL' && isV32) {
      return (
        <>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Rail Type</InputLabel>
              <Select
                value={leg.railType}
                onChange={(e) => {
                  updateLeg(leg.id, 'railType', e.target.value);
                  updateLeg(leg.id, 'truckType', '');
                }}
                label="Rail Type"
              >
                <MenuItem value="RAIL">RAIL</MenuItem>
                <MenuItem value="RAILRH">RAILRH</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {leg.railType === 'RAILRH' && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Truck Type</InputLabel>
                <Select value={leg.truckType} onChange={(e) => updateLeg(leg.id, 'truckType', e.target.value)} label="Truck Type">
                  {truckBaseCodes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          )}
        </>
      );
    }

    if (leg.type === 'FERRY') {
      const ferryOpts = (() => {
        if (isV32) return ferryTypeFCP;
        if (shipmentData.apiVersion === '2.8') return ferryTypeEN;
        return ferryTypeISO;
      })();
      return (
        <>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Truck Type</InputLabel>
              <Select value={leg.truckType} onChange={(e) => updateLeg(leg.id, 'truckType', e.target.value)} label="Truck Type">
                {truckBaseCodes.map(c => <MenuItem key={c} value={`${c}D`}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Ferry Type</InputLabel>
              <Select value={leg.ferryType} onChange={(e) => updateLeg(leg.id, 'ferryType', e.target.value)} label="Ferry Type">
                {ferryOpts.map(t => <MenuItem key={t} value={t}>{t.toUpperCase().replaceAll('_', ' ')}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </>
      );
    }

    if (leg.type === 'IWW') {
      const iwwOpts = (() => {
        if (isV32) return iwwTypeFCP;
        if (shipmentData.apiVersion === '2.8') return iwwTypeEN;
        return iwwTypeISO;
      })();
      return (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>IWW Type</InputLabel>
            <Select value={leg.iwwType} onChange={(e) => updateLeg(leg.id, 'iwwType', e.target.value)} label="IWW Type">
              {iwwOpts.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      );
    }

    return null;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>

      {/* Page header row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600} color="text.primary">
          Multi-Leg XML API Request
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* API Version — plain Box pill identical in structure to company badge */}
          <Box
            onClick={(e) => setVersionMenuAnchor(e.currentTarget)}
            sx={{
              px: 1.75, py: 1,
              border: '1.5px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              display: 'flex', alignItems: 'center', gap: 0.75,
              bgcolor: 'primary.50',
              cursor: 'pointer',
              userSelect: 'none',
              transition: 'background-color 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
              '&:hover': {
                bgcolor: 'rgba(144,191,69,0.15)',
                borderColor: 'primary.dark',
                boxShadow: '0 0 0 3px rgba(144,191,69,0.25)',
              },
            }}
          >
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ lineHeight: 1 }}>
              API Version
            </Typography>
            <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ lineHeight: 1 }}>
              {shipmentData.apiVersion}
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: '1rem', color: 'primary.main', ml: -0.25 }} />
          </Box>
          <Menu
            anchorEl={versionMenuAnchor}
            open={Boolean(versionMenuAnchor)}
            onClose={() => setVersionMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {apiVersions.map(v => (
              <MenuItem
                key={v}
                selected={v === shipmentData.apiVersion}
                onClick={() => { updateShipment('apiVersion', v); setVersionMenuAnchor(null); }}
              >
                {v}
              </MenuItem>
            ))}
          </Menu>

          {/* Error Codes Reference */}
          <Box
            onClick={fetchErrorCodes}
            sx={{
              px: 1.75, py: 1,
              border: '1.5px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              display: 'flex', alignItems: 'center', gap: 0.75,
              bgcolor: 'primary.50',
              cursor: 'pointer',
              userSelect: 'none',
              transition: 'background-color 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
              '&:hover': {
                bgcolor: 'rgba(144,191,69,0.15)',
                borderColor: 'primary.dark',
                boxShadow: '0 0 0 3px rgba(144,191,69,0.25)',
              },
            }}
          >
            <InfoIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
            <Typography variant="caption" fontWeight={600} color="primary.main" sx={{ lineHeight: 1 }}>
              Error Codes
            </Typography>
          </Box>

          {selectedCompany && (
            <Box sx={{
              px: 1.75, py: 1,
              border: '1.5px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              display: 'flex', alignItems: 'center', gap: 0.75,
              bgcolor: 'primary.50',
            }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ lineHeight: 1 }}>
                Company
              </Typography>
              <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ lineHeight: 1 }}>
                {selectedCompany.name}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Grid container spacing={2}>

        {/* ── Company & API Key ── */}
        <Grid item xs={12}>
          <Accordion {...sectionAccordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5, minHeight: 52 }}>
              <Typography variant="subtitle1" fontWeight={600}>Company & API Key</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
              {companiesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {shouldShowCompanySelector ? (
                    <Grid item xs={12}>
                      <LicenseeFilter
                        value={selectedCompany?.id || ''}
                        onChange={(id) => { if (id !== '') handleCompanySelect(id); }}
                        label="Select Company"
                        required
                        error={submitted && !selectedCompany}
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                  ) : selectedCompany && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">Company:</Typography>
                        <Typography variant="body1" fontWeight={600} color="primary.main">
                          {selectedCompany.name}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">ID {selectedCompany.id}</Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="API Key"
                      value={shipmentData.apiKey}
                      onChange={(e) => updateShipment('apiKey', e.target.value)}
                      disabled={!!selectedCompany}
                      required
                      error={submitted && !shipmentData.apiKey}
                      helperText={selectedCompany
                        ? 'Automatically populated from the selected company'
                        : 'Enter an API key, or select a company above'}
                    />
                  </Grid>
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* ── Shipment Details ── */}
        <Grid item xs={12}>
          <Accordion defaultExpanded {...sectionAccordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5, minHeight: 52 }}>
              <Typography variant="subtitle1" fontWeight={600}>Shipment Details</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Shipment ID"
                    value={shipmentData.shipmentId}
                    onChange={(e) => {
                      if (e.target.value.length <= 35) {
                        updateShipment('shipmentId', e.target.value);
                      }
                    }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Branch Code"
                    value={shipmentData.branch}
                    onChange={(e) => {
                      if (e.target.value.length <= 35) {
                        updateShipment('branch', e.target.value);
                      }
                    }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Sub Company Code"
                    value={shipmentData.subCompany}
                    onChange={(e) => {
                      if (e.target.value.length <= 35) {
                        updateShipment('subCompany', e.target.value);
                      }
                    }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Sub Customer Code"
                    value={shipmentData.customerReference}
                    onChange={(e) => {
                      if (e.target.value.length <= 35) {
                        updateShipment('customerReference', e.target.value);
                      }
                    }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Shipment Reference"
                    value={shipmentData.reference}
                    onChange={(e) => {
                      if (e.target.value.length <= 35) {
                        updateShipment('reference', e.target.value);
                      }
                    }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CarbonCareDatePicker fullWidth label="Date of Shipment" required
                    value={shipmentData.dateOfShipment || null}
                    onChange={(value) => updateShipment('dateOfShipment', value ? value + 'T00:00:00' : '')}
                    error={submitted && !shipmentData.dateOfShipment}
                    minDate={MIN_SHIPMENT_DATE}
                    maxDate={getMaxDateBoundary()}
                    startAdornment={
                      <InputAdornment position="start">
                        <CalendarIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                      </InputAdornment>
                    } />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Commodity"
                    value={shipmentData.commodity}
                    onChange={(e) => updateShipment('commodity', e.target.value)} />
                </Grid>
                <Grid item xs={8} sm={5}>
                  <TextField fullWidth label="Weight" type="number" required
                    value={shipmentData.weight}
                    onChange={(e) => {
                      if (e.target.value.length <= 10) {
                        updateShipment('weight', e.target.value);
                      }
                    }}
                    error={submitted && !shipmentData.weight}
                    inputProps={{ min: 0, max: 9999999999, step: 'any' }} />
                </Grid>
                <Grid item xs={4} sm={1}>
                  <FormControl fullWidth error={submitted && !shipmentData.weightUnit}>
                    <InputLabel>Unit</InputLabel>
                    <Select value={shipmentData.weightUnit} onChange={(e) => updateShipment('weightUnit', e.target.value)} label="Unit" required>
                      {weightUnits.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox checked={shipmentData.cooling} onChange={(e) => updateShipment('cooling', e.target.checked)} />}
                    label="Cooling Required"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* ── Transport Legs ── */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, mt: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={600} color="text.primary">
              Transport Legs
            </Typography>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addLeg}>
              Add Leg
            </Button>
          </Box>

          {shipmentData.legs.map((leg, index) => (
            <Accordion key={leg.id} defaultExpanded={index === 0} {...legAccordionSx}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ px: 2, minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1, py: 0.25,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        fontWeight: 700,
                        color: 'text.secondary',
                        letterSpacing: 0.5,
                      }}
                    >
                      {index + 1}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {leg.type}
                    </Typography>
                  </Box>
                  {shipmentData.legs.length > 1 && (
                    <IconButton
                      onClick={(e) => { e.stopPropagation(); removeLeg(leg.id); }}
                      color="error"
                      size="small"
                      sx={{ mr: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 1 }}>
                <Grid container spacing={2}>

                  {/* Mode + Distance on same row */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={submitted && !leg.type}>
                      <InputLabel>Transport Mode</InputLabel>
                      <Select value={leg.type} onChange={(e) => handleLegTypeChange(leg.id, e.target.value)} label="Transport Mode" required>
                        {legTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Distance (km)" type="number"
                      value={leg.distance ?? ''}
                      onChange={(e) => {
                        if (e.target.value.length <= 10) {
                          updateLeg(leg.id, 'distance', e.target.value ? Number(e.target.value) : undefined);
                        }
                      }}
                      inputProps={{ min: 0, max: 9999999999, step: 'any' }} />
                  </Grid>

                  {/* Transport-specific controls */}
                  {renderTransportFields(leg)}

                  {/* From / To locations */}
                  <Grid item xs={12} sx={{ mt: 0.5 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2, borderRadius: 1.5, bgcolor: 'grey.50',
                            ...(submitted && !isAddressValid(leg, 'from') && {
                              borderColor: '#d32f2f',
                              borderWidth: 2,
                            }),
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            color={submitted && !isAddressValid(leg, 'from') ? 'error' : 'text.secondary'}
                            sx={{ display: 'block', mb: 1.5, letterSpacing: 0.5, textTransform: 'uppercase' }}
                          >
                            From *
                          </Typography>
                          {renderAddressFields(leg, 'from')}
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2, borderRadius: 1.5, bgcolor: 'grey.50',
                            ...(submitted && !isAddressValid(leg, 'to') && {
                              borderColor: '#d32f2f',
                              borderWidth: 2,
                            }),
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            color={submitted && !isAddressValid(leg, 'to') ? 'error' : 'text.secondary'}
                            sx={{ display: 'block', mb: 1.5, letterSpacing: 0.5, textTransform: 'uppercase' }}
                          >
                            To *
                          </Typography>
                          {renderAddressFields(leg, 'to')}
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>

                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>

        {/* Alerts */}
        {error && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
          </Grid>
        )}
        {success && (
          <Grid item xs={12}>
            <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
          </Grid>
        )}

        {/* Submit */}
        <Grid item xs={12} sx={{ mt: 0.5, mb: 3 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} /> : <SendIcon />}
            fullWidth
            sx={{ py: 1.25, borderRadius: 1.5, fontWeight: 600 }}
          >
            {loading ? 'Submitting…' : 'Submit XML API Request'}
          </Button>
        </Grid>

        {/* Response */}
        {xmlResponse && (
          <Grid item xs={12}>
            <XmlResponseViewer xmlResponse={xmlResponse} />
          </Grid>
        )}
      </Grid>

      {/* Error Codes Dialog */}
      <Dialog
        open={errorCodesOpen}
        onClose={() => setErrorCodesOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Error Codes Reference
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            if (errorCodesLoading) return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
            );
            if (errorCodes) return (
            <List dense disablePadding>
              {Object.entries(errorCodes).map(([code, message], idx, arr) => (
                <React.Fragment key={code}>
                  <ListItem sx={{ py: 0.75 }}>
                    <ListItemText
                      primary={
                        typeof message === 'object' && message !== null
                          ? Object.entries(message).map(([dir, msg]) => (
                              <Typography key={dir} variant="body2" component="div">
                                <Typography component="span" variant="body2" fontWeight={700}>Code {code}</Typography>
                                {' - '}{dir}: {String(msg)}
                              </Typography>
                            ))
                          : (
                            <Typography variant="body2">
                              <Typography component="span" variant="body2" fontWeight={700}>Code {code}</Typography>
                              {' - '}{String(message)}
                            </Typography>
                          )
                      }
                    />
                  </ListItem>
                  {idx < arr.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
            );
            return (
            <Typography color="text.secondary">Failed to load error codes.</Typography>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default XmlApiForm;
